import { Modal } from 'antd';
import { type FC, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { type IControl, type MapInstance, useControl } from 'react-map-gl/mapbox';
import { IconExpand, IconQuestionCircle } from 'src/components/Icon';

interface MapToolsControlProps {
  onFitToAll?: () => void;
}

class MapToolsControlClass implements IControl {
  _map: MapInstance | undefined;
  _container: HTMLElement | null = null;
  _onHelp: () => void;
  onFitToAll?: () => void;

  constructor(onHelp: () => void, onFitToAll?: () => void) {
    this._onHelp = onHelp;
    this.onFitToAll = onFitToAll;
  }

  onAdd(map: MapInstance) {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    this._container.addEventListener('contextmenu', (e) => e.preventDefault());

    if (this.onFitToAll != null) {
      const fitBtn = document.createElement('button');
      fitBtn.type = 'button';
      fitBtn.title = 'Zoom to all items';
      fitBtn.style.fontSize = '1.1em';
      fitBtn.style.paddingTop = '4px';
      fitBtn.innerHTML = renderToString(<IconExpand />);
      fitBtn.addEventListener('click', () => this.onFitToAll?.());
      this._container.appendChild(fitBtn);
    }

    const helpBtn = document.createElement('button');
    helpBtn.type = 'button';
    helpBtn.title = 'Editing help';
    helpBtn.style.fontSize = '1.1em';
    helpBtn.style.paddingTop = '4px';
    helpBtn.innerHTML = renderToString(<IconQuestionCircle />);
    helpBtn.addEventListener('click', () => this._onHelp());
    this._container.appendChild(helpBtn);

    return this._container;
  }

  onRemove() {
    this._container?.parentNode?.removeChild(this._container);
    this._map = undefined;
  }
}

export const MapToolsControl: FC<MapToolsControlProps> = ({ onFitToAll }) => {
  const [open, setOpen] = useState(false);

  const ctrl = useControl<MapToolsControlClass>(
    () => new MapToolsControlClass(() => setOpen(true), onFitToAll),
    { position: 'top-right' },
  );
  // Keep the click handler fresh — the bound features change as the user edits.
  ctrl.onFitToAll = onFitToAll;

  return (
    <Modal
      title="Editing the map"
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      width={520}
    >
      <ul style={{ paddingLeft: '1.2em', marginBottom: 0 }}>
        <li>Click a track, split or point of interest to select it.</li>
        <li>
          <strong>Hold Shift and click</strong> to select several elements at once, then move or
          remove them together.
        </li>
        <li>Drag a selected element to move it.</li>
        <li>
          Select a single track and click <strong>Modify points</strong> to edit its shape. While
          modifying, <strong>Shift+Click</strong> vertices to select several.
        </li>
        <li>Use the fit button to zoom to all items on the map.</li>
      </ul>
    </Modal>
  );
};
